import React, { useState, useEffect } from 'react';
import { PropsWithChildren, useContext } from 'react';
import agent from '../agent';
import types, {
  DisplayCourseType,
  OfficeProfileViewType,
  StaffProfileViewType,
  StaffResumeViewType,
  UserCertType,
  UserCompanyType,
  UserSchoolType,
} from '../types/services-api';
import analytics from '../analytics';
import JobPostingContext from './JobPostingContext';
import {
  isEmployerType,
  isStaffType,
  UserContext,
  UserContextType,
} from './UserContext';
import InvitationsContext from './InvitationsContext';
import { computeProfileCompletion } from '../utils/resumeProfileHelpers';
import { sanitizeCompanyDates } from '../utils/collectionHelper';
import axios from 'axios';
import CandidateContext from './CandidateContext';
import ApplicationsContext from './ApplicationsContext';
import {
  ParsedCertResponse,
  ParsedCompanyResponse,
  ParsedCourseResponse,
  ResumeUpdatePayload,
  ParsedSchoolResponse,
  ParseResumeResponse,
} from '../types/amazon-bedrock';
import { navigate } from 'gatsby';

export type ProfileType =
  | types.EmployerProfileViewType
  | types.StaffResumeViewType;

export function isEmployerProfileType(
  object: any
): object is types.EmployerProfileViewType {
  return !!object && 'about' in object;
}

export function isStaffProfileType(
  object: any
): object is types.StaffResumeViewType {
  return !!object && 'certs' in object;
}

type SharedProfileContextType = {
  updateProfile: (updatedProfileValues: {}) => void;
  fetchError: string;
};

export interface UnauthenticatedProfileContextType extends SharedProfileContextType {
  profile: null;
}

export type ResumeListType = 'companies' | 'schools' | 'certs' | 'courses';
export type ExistingResumeListItemType =
  | UserCompanyType
  | UserSchoolType
  | UserCertType
  | DisplayCourseType;

export type ParsedResumeListItemType =
  | ParsedCompanyResponse
  | ParsedSchoolResponse
  | ParsedCertResponse
  | ParsedCourseResponse;

export type ResumeListItemType =
  | ExistingResumeListItemType
  | ParsedResumeListItemType;

export type TempResumeListItemType = Partial<ResumeListItemType> & {
  tempId: number;
};

export function isExistingItem(obj: any): obj is ExistingResumeListItemType {
  return 'id' in obj;
}

export function isTempItem(obj: any): obj is TempResumeListItemType {
  //assign a temp ID to items that haven't been saved to the database yet (i.e. don't have an ID) so they can be edited/deleted in the UI before saving to the database
  return 'tempId' in obj;
}

export function isParsedItem(obj: any): obj is ParsedResumeListItemType {
  return !isExistingItem(obj) && !isTempItem(obj);
}

export interface StaffProfileContextType extends SharedProfileContextType {
  profile: types.StaffResumeViewType;
  parsedResume: ParseResumeResponse | undefined;
  setParsedResume: React.Dispatch<
    React.SetStateAction<ParseResumeResponse | undefined>
  >;
  parsingError: string;
  setParsingError: React.Dispatch<React.SetStateAction<string>>;
  saveResume: ({
    updatedItems,
    nextPath,
  }: {
    updatedItems: Partial<StaffProfileViewType | StaffResumeViewType>;
    nextPath: string;
  }) => void;
  updateResumeListItem: (props: {
    itemKey: ResumeListType;
    updatedItem: ExistingResumeListItemType | TempResumeListItemType;
    originalItem?: ResumeListItemType | TempResumeListItemType;
  }) => void;
  deleteResumeListItem: (props: {
    itemKey: ResumeListType;
    item: ResumeListItemType | TempResumeListItemType;
  }) => void;
}

export interface EmployerProfileContextType extends SharedProfileContextType {
  profile: types.EmployerProfileViewType;
}

export type ProfileContextType =
  | StaffProfileContextType
  | EmployerProfileContextType
  | UnauthenticatedProfileContextType;

export type OfficesContextType = {
  offices: types.OfficeProfileViewType[];
  primaryOffice: types.OfficeProfileViewType | null;
  updateOffices: (updatedOffices: types.OfficeProfileViewType[]) => void;
  updatePrimaryOffice: (officeId: number) => void;
  upsertOffice: (updatedOffice: types.OfficeProfileViewType) => void;
  removeOffice: (officeId: number) => void;
  purchaseHistoryList: types.PaymentReceiptType[];
  fetchReceipts: () => void;
};

export const initialContext: UnauthenticatedProfileContextType = {
  profile: null,
  fetchError: '',
  updateProfile: () => {},
};

export const ProfileContext =
  React.createContext<ProfileContextType>(initialContext);

export const OfficesContext = React.createContext<OfficesContextType>({
  offices: [],
  primaryOffice: null,
  updateOffices: () => {},
  updatePrimaryOffice: () => {},
  upsertOffice: () => {},
  removeOffice: () => {},
  purchaseHistoryList: [],
  fetchReceipts: () => {},
});

const ProfileContextWrapper = (props: PropsWithChildren) => {
  const { user } = useContext(UserContext);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const [purchaseHistoryList, setPurchaseHistoryList] = useState<
    types.PaymentReceiptType[]
  >([]);
  const [parsedResume, setParsedResume] = useState<
    ParseResumeResponse | undefined
  >(undefined);
  const [parsingError, setParsingError] = useState('');
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    if (isEmployerType(user)) {
      axios
        .all([fetchEmployerProfile(), fetchOffices()])
        .then(
          axios.spread(function (profileRes, officesRes) {
            if (
              isEmployerType(user) &&
              isEmployerProfileType(profileRes.data)
            ) {
              const primary = officesRes.data.find(
                (office: OfficeProfileViewType) => office.isPrimary
              );
              analytics.User.setUser({
                userId: user.id,
                email: user.email,
                role: 'employer',
                empType: user.empType,
                name: user.name,
                position: 'N/A',
                specialty: 'N/A',
                availability: 'N/A',
                tempAvailability: 'N/A',
                isPublic: profileRes.isPublic,
                userProfileComplete: 0,
                userState: !!primary ? primary.state : officesRes.data[0].state,
                userZipCode: !!primary ? primary.zip : officesRes.data[0].zip,
              });
            }
          })
        )
        .catch((err) => setFetchError(err.message));
      fetchReceipts();
    } else if (isStaffType(user)) {
      fetchStaffProfile();
    }
  }, []);

  function fetchEmployerProfile() {
    return agent.Employer.profile()
      .then((res) => {
        setProfile({ ...res.data });
        return res;
      })
      .catch((err) => {
        setFetchError(err.message);
        return err;
      });
  }

  function fetchReceipts() {
    agent.Employer.receipts()
      .then((res) => {
        setPurchaseHistoryList(res.data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function fetchStaffProfile() {
    agent.Staff.profile()
      .then((res) => {
        setProfile({
          ...res.data,
        });
        if (isStaffType(user) && isStaffProfileType(res.data)) {
          const profileCompletePercentage = computeProfileCompletion({
            staffData: user,
            resumeProfileData: res.data,
          }).percentComplete;

          analytics.User.setUser({
            userId: user.id,
            email: user.email,
            role: 'staff',
            empType: 'N/A',
            name: user.name,
            position: user.position,
            specialty: user.specialties,
            availability: res.data.dowTotal,
            tempAvailability: res.data.dowTotalTemp,
            isPublic: res.data.isPublic,
            isRelocate: res.data.isRelocate,
            userProfileComplete: profileCompletePercentage,
            userState: user.state,
            userZipCode: user.zip,
          });
        }
      })
      .catch((err) => {
        setFetchError(err.message);
      });
  }

  function saveResume({
    updatedItems,
    nextPath,
  }: {
    updatedItems: Partial<StaffProfileViewType | StaffResumeViewType>;
    nextPath: string;
  }) {
    if (!isStaffProfileType(profile)) return;
    const body = {
      ...user,
      ...profile,
      skills: profile.skills.map((skill) => skill.skill),
      ...updatedItems,
      companies: (
        'companies' in updatedItems
          ? updatedItems.companies
          : profile.companies
      )?.map(sanitizeCompanyDates),
      schools:
        'schools' in updatedItems ? updatedItems.schools : profile.schools,
      certs: 'certs' in updatedItems ? updatedItems.certs : profile.certs,
      courses:
        'courses' in updatedItems ? updatedItems.courses : profile.courses,
    } as ResumeUpdatePayload;
    agent.Staff.saveResume({ body })
      .then((res) => {
        navigate(nextPath);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function updateProfile(updatedProfileValues: Partial<ProfileType>) {
    const updatedProfile = {
      ...profile,
      ...updatedProfileValues,
    } as ProfileType;
    setProfile(updatedProfile);
  }

  function updateResumeListItem({
    originalItem,
    updatedItem,
    itemKey,
  }: {
    originalItem?: ResumeListItemType | TempResumeListItemType;
    updatedItem: ExistingResumeListItemType | TempResumeListItemType;
    itemKey: ResumeListType;
  }) {
    if (!isStaffProfileType(profile)) return;
    if (!originalItem && !!updatedItem) {
      //Adding a new item
      updateProfile({
        ...profile,
        [itemKey]: [...profile[itemKey], updatedItem],
      });
    } else if (!!originalItem) {
      if (isParsedItem(originalItem) && !!parsedResume) {
        //because parsed items are transformed into profile item format on edit/save,
        //update existing parsed item by removing from parsedResume list and adding to profile list
        const updatedItemList = [
          ...parsedResume[itemKey].filter(
            (i) => JSON.stringify(i) !== JSON.stringify(originalItem)
          ),
        ];
        setParsedResume({
          ...parsedResume,
          [itemKey]: updatedItemList,
        } as ParseResumeResponse);
        updateProfile({
          ...profile,
          [itemKey]: [...profile[itemKey], updatedItem],
        });
      } else if (!isParsedItem(originalItem)) {
        //Updating existing or temp item
        updateProfile({
          ...profile,
          [itemKey]: [
            ...profile[itemKey].map((i) => {
              return (
                isTempItem(i) && isTempItem(originalItem)
                  ? i.tempId === originalItem.tempId
                  : isExistingItem(i) && isExistingItem(originalItem)
                    ? i.id === originalItem.id
                    : false
              )
                ? { ...i, ...updatedItem }
                : i;
            }),
          ],
        });
      }
    }
  }

  function deleteResumeListItem({
    itemKey,
    item,
  }: {
    itemKey: ResumeListType;
    item: ResumeListItemType | TempResumeListItemType;
  }) {
    if (!isStaffProfileType(profile)) return;
    if (isParsedItem(item) && !!parsedResume) {
      const list = parsedResume[itemKey];
      setParsedResume({
        ...parsedResume,
        [itemKey]: list.filter(
          (i) => JSON.stringify(i) !== JSON.stringify(item)
        ),
      });
    } else {
      updateProfile({
        ...profile,
        [itemKey]: profile[itemKey].filter((i) =>
          isTempItem(item)
            ? !isTempItem(i) || i.tempId !== item.tempId
            : !isExistingItem(item) || i.id !== item.id
        ),
      });
    }
  }

  // ****** Offices ********
  const { setDefaultMapCenter } = useContext(UserContext) as UserContextType;
  const [offices, setOffices] = useState<types.OfficeProfileViewType[]>([]);
  const [primaryOffice, setPrimaryOffice] =
    useState<types.OfficeProfileViewType | null>(null);

  useEffect(() => {
    if (!!primaryOffice) {
      setDefaultMapCenter({
        lat: Number(primaryOffice.lat),
        lng: Number(primaryOffice.lng),
      });
    }
  }, [primaryOffice]);

  function fetchOffices() {
    return agent.Employer.offices()
      .then((res) => {
        setOffices(res.data);
        const primary = res.data.find((office) => office.isPrimary);
        if (!!primary) {
          setPrimaryOffice(primary);
        } else {
          setPrimaryOffice(res.data[0]);
        }
        return res;
      })
      .catch((err) => {
        console.log(err.message);
        setOffices([]);
        return err;
      });
  }

  function updateOffices(updatedOffices: types.OfficeProfileViewType[]) {
    setOffices(updatedOffices);
  }

  function upsertOffice(office: types.OfficeProfileViewType) {
    let updatedList: types.OfficeProfileViewType[];
    const itemExists = offices.some((i) => i.id === office.id);
    if (itemExists) {
      updatedList = offices.map((i: types.OfficeProfileViewType) =>
        i.id === office.id ? office : i
      );
    } else {
      updatedList = [...offices, office];
    }

    if (office.isPrimary) {
      setPrimaryOffice(office);
    }
    updateOffices(updatedList);
  }

  function updatePrimaryOffice(officeId: number) {
    agent.Employer.updatePrimaryOffice({ officeId })
      .then((res) => {
        updateOffices([
          res.data,
          ...offices.filter((office) => office.id !== res.data.id),
        ]);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function removeOffice(officeId: number) {
    const updatedList: types.OfficeProfileViewType[] = offices.filter(
      (i) => i.id !== officeId
    );
    updateOffices(updatedList);
  }

  //for DevTools to display context name
  ProfileContext.displayName = 'Profile Context';
  OfficesContext.displayName = 'Offices Context';

  return (
    <ProfileContext.Provider
      value={{
        profile,
        parsedResume,
        setParsedResume,
        saveResume,
        updateProfile,
        updateResumeListItem,
        deleteResumeListItem,
        parsingError,
        setParsingError,
        fetchError,
      }}
    >
      {isEmployerType(user) && (
        <OfficesContext.Provider
          value={{
            offices,
            primaryOffice,
            updateOffices,
            updatePrimaryOffice,
            upsertOffice,
            removeOffice,
            purchaseHistoryList,
            fetchReceipts,
          }}
        >
          <JobPostingContext>
            <CandidateContext>{props.children}</CandidateContext>
          </JobPostingContext>
        </OfficesContext.Provider>
      )}
      {isStaffType(user) && (
        <ApplicationsContext>
          <InvitationsContext>{props.children}</InvitationsContext>
        </ApplicationsContext>
      )}
    </ProfileContext.Provider>
  );
};

export default ProfileContextWrapper;
